import {
  DEVICE_ALERT_WINDOW_DAYS,
  type DevicePersonIdentity,
} from '@kirin/api-client/contracts/device-inventory';
import { ORGANIZATION_ALERTS_FILTERABLE_FIELDS } from '@kirin/api-client/contracts/alerts';
import { encodeTitleFilterKey } from '@kirin/api-client/helpers/alerts';
import { FilterOperators } from '@kirin/api-client/contracts/table';
import { USER_SECURITY_POSTURE_FILTERABLE_FIELDS } from '@kirin/api-client/contracts/user-security-posture';
import { DATA_TABLE_PERSISTENCE_KEYS } from '@/components/DataTable/types';
import { PostureManagementTabs } from '@/components/Inventory/constants';
import {
  buildAlertsDeepLinkUrl,
  createFilter,
} from '@/pages/organization/tabs/DataTableAlertsTab/utils';
import {
  USP_FILTER_URL_KEYS,
  USP_TABLE_CONFIG,
} from '@/pages/organization/tabs/DataTableUsersTab/constants';

/** Inventory → Endpoints. */
export function buildEndpointsTabPath(organizationId: string): string {
  return `/${organizationId}/inventory?tab=${PostureManagementTabs.ENDPOINTS}`;
}

/** The endpoint page of one device. */
export function buildEndpointPagePath(
  organizationId: string,
  deviceId: string
): string {
  return `/${organizationId}/inventory/endpoints/${encodeURIComponent(deviceId)}`;
}

/** Opens the endpoint page of the machine an endpoint ID belongs to. */
export function buildEndpointLookupPath(
  organizationId: string,
  machineId: string
): string {
  return `/${organizationId}/inventory/endpoints/by-endpoint-id/${encodeURIComponent(machineId)}`;
}

/**
 * People with a row on Inventory → Users: Kirin accounts (`type = 'full'`),
 * active or offboarded. Discovered and Unattributed people are not listed.
 */
const USERS_TAB_IDENTITIES: ReadonlySet<DevicePersonIdentity> = new Set([
  'member',
  'offboarded',
]);

/**
 * A person's row on Inventory → Users: filtered to their email and expanded.
 * Null for anyone the Users tab does not list (Discovered, Unattributed) or
 * without an email, so the link never opens an empty table.
 */
export function buildPersonUsersPath(
  organizationId: string,
  person: {
    userId: string;
    email: string | null;
    identity: DevicePersonIdentity;
  }
): string | null {
  if (!person.email || !USERS_TAB_IDENTITIES.has(person.identity)) return null;
  const { tableId, defaultOperator } = USP_TABLE_CONFIG;
  const params = new URLSearchParams({
    tab: PostureManagementTabs.USER_SECURITY_POSTURE,
  });
  const emailKey =
    USP_FILTER_URL_KEYS[USER_SECURITY_POSTURE_FILTERABLE_FIELDS.EMAIL];
  params.set(
    `${tableId}.${emailKey}[${defaultOperator}]`,
    encodeURIComponent(person.email)
  );
  params.set(
    `${tableId}.${DATA_TABLE_PERSISTENCE_KEYS.url.expanded}`,
    person.userId
  );
  return `/${organizationId}/inventory?${params.toString()}`;
}

/**
 * Active alerts seen on these endpoint IDs (by this person, when given) in the
 * last 7 days, optionally of one alert title: exactly what the Endpoints tab
 * counts. An alert seen here only before the window is left out, even when it
 * was seen on another machine since (KIR-5537). The title filter applies where the Alerts page has a Title column.
 * Link only while `endpoint_identifier` is on: without it the Alerts page
 * drops the endpoint filter and the link would open every alert in the org.
 */
export function buildEndpointAlertsUrl(
  organizationId: string,
  machineIds: string[],
  now: number,
  options: {
    userId?: string;
    alert?: { subPolicyKey: string | null; type: string };
  } = {}
): string {
  const since = new Date(
    now - DEVICE_ALERT_WINDOW_DAYS * 24 * 60 * 60 * 1000
  ).toISOString();
  const { userId, alert } = options;
  return (
    buildAlertsDeepLinkUrl(
      organizationId,
      [
        createFilter(
          ORGANIZATION_ALERTS_FILTERABLE_FIELDS.ENDPOINTS,
          machineIds
        ),
        ...(userId
          ? [
              createFilter(ORGANIZATION_ALERTS_FILTERABLE_FIELDS.USERS, [
                userId,
              ]),
            ]
          : []),
        ...(alert
          ? [
              createFilter(ORGANIZATION_ALERTS_FILTERABLE_FIELDS.TITLE, [
                encodeTitleFilterKey(alert.subPolicyKey, alert.type),
              ]),
            ]
          : []),
        // KIR-5537: seen on THESE machines in the window, not anywhere.
        createFilter(
          ORGANIZATION_ALERTS_FILTERABLE_FIELDS.ENDPOINT_LAST_SEEN_AT,
          [since],
          FilterOperators.GREATER_THAN
        ),
      ],
      { visibility: 'active' }
    ) ?? `/${organizationId}/alerts`
  );
}
